'use client';

import { SvgChevronright } from '@common/icon';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useRef } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';

import { MegaphoneIcon, NewBadgeIcon } from '../Icons';

interface Notice {
  communityId: number;
  communityIdIdx: string;
  title: string;
  daysAgo: string;
  createdAt?: string;
}

interface NoticeTopBannerClientProps {
  notices: Notice[];
}

const isNew = (daysAgo: string): boolean => {
  // "N일 전", "N시간 전", "N분 전" 등의 문자열을 파싱
  const match = daysAgo.match(/(\d+)(일|시간|분)/);
  if (!match) return false;

  const value = Number.parseInt(match[1], 10);
  const unit = match[2];

  if (unit === '분') return true; // 1시간 미만
  if (unit === '시간') return value <= 72; // 72시간 이내
  if (unit === '일') return value <= 2; // 2일 이내

  return false;
};

export const NoticeTopBannerClient = ({
  notices,
}: NoticeTopBannerClientProps) => {
  const autoplayRef = useRef(
    Autoplay({ delay: 2000, stopOnInteraction: false }),
  );

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      axis: 'y',
    },
    [autoplayRef.current],
  );

  if (notices.length === 0) {
    return null;
  }

  if (notices.length === 1) {
    const notice = notices[0];

    return (
      <CustomLink
        href={ROUTES.WELLO_NEWS_DETAIL.pathname({
          idIdx: notice.communityIdIdx,
        })}
        className="group flex items-center justify-between px-28 py-8"
      >
        <div className="flex min-w-0 flex-1 items-center gap-8">
          <MegaphoneIcon className="shrink-0" />

          <p className="max-w-[95%] truncate font-600 text-Gray-700 transition-colors duration-200 typo-caption1 group-hover:text-Gray-900">
            {notice.title}
          </p>

          {isNew(notice.daysAgo) && <NewBadgeIcon className="shrink-0" />}
        </div>
        <div className="ml-8 shrink-0 transition-transform duration-200 group-hover:translate-x-4">
          <SvgChevronright className="size-16 text-Gray-500 transition-colors duration-200 group-hover:text-Gray-700" />
        </div>
      </CustomLink>
    );
  }

  return (
    <div className="relative h-36">
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full flex-col">
          {notices.map((notice) => (
            <div key={notice.communityId} className="min-h-0 flex-[0_0_100%]">
              <CustomLink
                href={ROUTES.WELLO_NEWS_DETAIL.pathname({
                  idIdx: notice.communityIdIdx,
                })}
                className="group flex h-full items-center justify-between px-28 py-8"
                onClick={() => autoplayRef.current.stop()}
              >
                <div className="flex min-w-0 flex-1 items-center gap-8">
                  <MegaphoneIcon className="shrink-0" />

                  <p className="max-w-[95%] truncate font-600 text-Gray-700 transition-colors duration-200 typo-caption1 group-hover:text-Gray-900">
                    {notice.title}
                  </p>

                  {isNew(notice.daysAgo) && (
                    <NewBadgeIcon className="shrink-0" />
                  )}
                </div>
                <div className="ml-8 shrink-0 transition-transform duration-200 group-hover:translate-x-4">
                  <SvgChevronright className="size-16 text-Gray-500 transition-colors duration-200 group-hover:text-Gray-700" />
                </div>
              </CustomLink>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
